import React, { useMemo, useState } from 'react';
import { apiRequest } from '../../../api/client';
import {
  PayrollConnectPicker,
  type PayrollConnectorInfo,
  type PayrollProviderId,
  type PayrollSettingsState
} from './PayrollConnectPicker';

type PayrollSettingsSectionProps = {
  settings: PayrollSettingsState;
  connectors: PayrollConnectorInfo[];
  loading: boolean;
  saving: boolean;
  disconnecting: boolean;
  onProviderChange: (provider: PayrollProviderId) => void;
  onSyncOnApproveChange: (value: boolean) => void;
  onNotifyEmailsChange: (emails: string) => void;
  onDisconnect: () => void;
  onSettingsRefresh: (payload: { settings: PayrollSettingsState; connectors: PayrollConnectorInfo[] }) => void;
};

function connectedConnectorName(
  connectors: PayrollConnectorInfo[],
  connectedConnector?: string
): string | null {
  if (!connectedConnector) return null;
  return connectors.find((c) => c.id === connectedConnector)?.name || connectedConnector;
}

export default function PayrollSettingsSection({
  settings,
  connectors,
  loading,
  saving,
  disconnecting,
  onProviderChange,
  onSyncOnApproveChange,
  onNotifyEmailsChange,
  onDisconnect,
  onSettingsRefresh
}: PayrollSettingsSectionProps): React.JSX.Element {
  const [pickerOpen, setPickerOpen] = useState(false);
  const notifyValue = settings.exportNotifyEmails.join(', ');

  const liveConnected = Boolean(settings.connectedConnector && settings.quickbooks.connected);
  const connectedName = connectedConnectorName(connectors, settings.connectedConnector);

  const queueSummary = useMemo(() => {
    const requested = connectors.filter((c) => c.requested && c.status !== 'connected');
    return requested;
  }, [connectors]);

  async function handleRequestConnector(connectorId: string, note?: string) {
    const res = await apiRequest<{
      settings: PayrollSettingsState;
      connectors: PayrollConnectorInfo[];
    }>('/api/settings/payroll/provider-interest', {
      method: 'POST',
      json: { connectorId, note }
    });
    onSettingsRefresh(res);
    setPickerOpen(false);
  }

  return (
    <div className="settings-tab-stack">
      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Payroll export</div>
        <p className="settings-hint">
          Export approved technician hours for your bookkeeper or payroll provider. CSV works with ADP,
          Wagepoint, and any system that accepts a spreadsheet import.
        </p>

        <div className="form-group">
          <label className="form-label" htmlFor="payroll-provider">
            Payroll method
          </label>
          <select
            id="payroll-provider"
            className="form-input"
            disabled={loading || saving}
            value={settings.provider}
            onChange={(e) => onProviderChange(e.target.value as PayrollProviderId)}
          >
            <option value="csv">CSV file export (recommended)</option>
            <option value="quickbooks" disabled={!liveConnected}>
              Live sync {liveConnected ? (connectedName ? `(${connectedName})` : '') : '(connect a provider first)'}
            </option>
          </select>
        </div>

        {settings.provider === 'csv' ? (
          <p className="settings-hint">
            After approving hours, download a CSV from the Hours approval tab and upload it to your payroll
            system.
          </p>
        ) : (
          <p className="settings-hint">
            Approved hours sync to your connected payroll provider. Map each technician&apos;s payroll employee
            ID in Team settings.
          </p>
        )}

        <ul className="settings-toggle-list">
          <li className="settings-toggle-row">
            <span className="settings-toggle-label">
              Sync to payroll when hours are approved
              {settings.provider === 'csv' ? ' (marks entries as exported)' : ' (live sync)'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={settings.syncOnApprove}
              className={`settings-switch ${settings.syncOnApprove ? 'is-on' : ''}`}
              disabled={loading || saving || (settings.provider === 'quickbooks' && !liveConnected)}
              onClick={() => onSyncOnApproveChange(!settings.syncOnApprove)}
            >
              <span className="settings-switch-thumb" />
            </button>
          </li>
        </ul>

        {settings.lastSyncAt ? (
          <p className="settings-hint">
            Last sync: {new Date(settings.lastSyncAt).toLocaleString()}
            {settings.lastSyncError ? ` — ${settings.lastSyncError}` : ''}
          </p>
        ) : null}
      </section>

      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Payroll connector</div>
        <p className="settings-hint">
          Optional live sync — connect your company&apos;s payroll account. CSV export always works without a
          connector.
        </p>

        {liveConnected ? (
          <div className="settings-payroll-qb-connected">
            <p className="settings-hint" style={{ marginBottom: 12 }}>
              Connected to <strong>{connectedName || 'payroll provider'}</strong>
              {settings.quickbooks.realmId?.startsWith('dev-') ? (
                <span className="hours-entry-kind" style={{ marginLeft: 8 }}>
                  Local dev
                </span>
              ) : null}
              {settings.quickbooks.realmId && !settings.quickbooks.realmId.startsWith('dev-') ? (
                <>
                  {' '}
                  — account <strong>{settings.quickbooks.realmId}</strong>
                </>
              ) : null}
              {settings.quickbooks.connectedAt
                ? ` since ${new Date(settings.quickbooks.connectedAt).toLocaleDateString()}`
                : ''}
              .
            </p>
            <div className="payroll-connector-actions">
              <button
                type="button"
                className="dash-btn dash-btn-ghost"
                disabled={disconnecting || loading}
                onClick={onDisconnect}
              >
                {disconnecting ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {!settings.liveSyncAvailable ? (
              <p className="settings-hint settings-payroll-unavailable">
                No live connectors are enabled on this server yet. Use CSV export, or request your provider
                below so we know what to build next.
              </p>
            ) : null}
            <button
              type="button"
              className="btn-primary"
              disabled={loading || saving}
              onClick={() => setPickerOpen(true)}
            >
              Connect payroll provider
            </button>
          </>
        )}

        {queueSummary.length > 0 ? (
          <p className="settings-hint" style={{ marginTop: 16 }}>
            Build queue for this workspace:{' '}
            <strong>{queueSummary.map((c) => c.name).join(', ')}</strong>
          </p>
        ) : null}
      </section>

      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Export notifications</div>
        <p className="settings-hint">Optional comma-separated emails to notify after a payroll export (coming soon).</p>
        <div className="form-group">
          <label className="form-label" htmlFor="payroll-notify-emails">
            Notify emails
          </label>
          <input
            id="payroll-notify-emails"
            className="form-input"
            type="text"
            placeholder="payroll@company.com, bookkeeper@company.com"
            disabled={loading || saving}
            value={notifyValue}
            onChange={(e) => onNotifyEmailsChange(e.target.value)}
          />
        </div>
      </section>

      {pickerOpen ? (
        <PayrollConnectPicker
          connectors={connectors}
          onClose={() => setPickerOpen(false)}
          onRequestConnector={handleRequestConnector}
        />
      ) : null}
    </div>
  );
}

// Re-export types used by SettingsPage
export type { PayrollSettingsState, PayrollConnectorInfo, PayrollProviderId } from './PayrollConnectPicker';
export { DEFAULT_PAYROLL_SETTINGS } from './PayrollConnectPicker';
